import { useState } from "react";

import {
  errorMessage,
  useExtendWorkspace,
  useStopWorkspace,
} from "../../api/queries";
import type { Workspace } from "../../api/types";
import { TERMINAL_WORKSPACE_STATES } from "../../api/types";
import { formatRemaining, pluralize } from "../../lib/format";
import { Dialog } from "../ui/Dialog";
import { ResizeDialog } from "./ResizeDialog";
import { ShortenDialog } from "./ShortenDialog";

/** Extension lengths offered in the dialog. */
const EXTEND_PRESETS: readonly number[] = [1, 2, 4, 8];

/**
 * States in which a workspace holds GPUs it could hand back.
 *
 * Before this it holds none, and the server says so rather than pretending.
 */
const RUNNING_WORKSPACE_STATES: readonly Workspace["state"][] = [
  "ready",
  "expiring",
];

interface WorkspaceActionsProps {
  workspace: Workspace;
  /** Called after the workspace was stopped. */
  onStopped?: () => void;
}

/**
 * What you do to a live workspace: change its GPUs, keep it longer, give time
 * back, stop it — with a confirmation for the destructive one.
 *
 * Keeping it longer and giving time back are the same lever in both
 * directions, and neither is a negotiation: there is no ceiling to extend
 * against, and nobody is asked to hand time back.
 *
 * Args:
 *     workspace: The workspace being acted on.
 *     onStopped: Optional callback once it is stopped.
 *
 * Returns:
 *     The action buttons and their dialogs.
 */
export function WorkspaceActions({
  workspace,
  onStopped,
}: WorkspaceActionsProps): JSX.Element | null {
  const [dialog, setDialog] = useState<
    "resize" | "extend" | "shorten" | "stop" | null
  >(null);
  const [hours, setHours] = useState<number>(EXTEND_PRESETS[1] ?? 2);
  const extend = useExtendWorkspace(workspace.id);
  const stop = useStopWorkspace(workspace.id);

  if (TERMINAL_WORKSPACE_STATES.includes(workspace.state)) {
    return null;
  }

  const close = (): void => {
    setDialog(null);
    extend.reset();
    stop.reset();
  };

  return (
    <div className="actions">
      <button type="button" className="btn" onClick={() => setDialog("resize")}>
        Change GPUs
      </button>
      <button type="button" className="btn" onClick={() => setDialog("extend")}>
        Keep it longer
      </button>
      {RUNNING_WORKSPACE_STATES.includes(workspace.state) ? (
        <button
          type="button"
          className="btn"
          onClick={() => setDialog("shorten")}
        >
          Give time back
        </button>
      ) : null}
      <button
        type="button"
        className="btn btn--danger"
        onClick={() => setDialog("stop")}
      >
        Stop
      </button>

      {dialog === "resize" ? (
        <ResizeDialog workspace={workspace} onClose={close} />
      ) : null}

      {dialog === "shorten" ? (
        <ShortenDialog workspace={workspace} onClose={close} />
      ) : null}

      {dialog === "extend" ? (
        <Dialog
          title="Keep this workspace longer"
          onClose={close}
          footer={
            <>
              <button type="button" className="btn" onClick={close}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={extend.isPending}
                onClick={() =>
                  extend.mutate({ hours }, { onSuccess: () => setDialog(null) })
                }
              >
                {extend.isPending ? "Adding…" : `Add ${pluralize(hours, "hour")}`}
              </button>
            </>
          }
        >
          <p className="dialog__text">
            It currently stops in {formatRemaining(workspace.expires_at)}. How
            much longer do you need it?
          </p>
          <p className="dialog__text">
            Add as much as you like, as many times as you like — there is no
            maximum on how long a workspace may run, so a job that turns out to
            take days is not a problem to solve here. What still ends it: this
            deadline, GPUs left unused for a while, and your own Stop button.
          </p>
          <fieldset className="choice-row">
            <legend className="sr-only">How many more hours</legend>
            {EXTEND_PRESETS.map((preset) => (
              <label key={preset} className="choice" data-selected={hours === preset}>
                <input
                  type="radio"
                  name="extend-hours"
                  className="sr-only"
                  value={preset}
                  checked={hours === preset}
                  onChange={() => setHours(preset)}
                />
                +{preset} h
              </label>
            ))}
          </fieldset>
          {extend.isError ? (
            <p className="dialog__error" role="alert">
              {errorMessage(extend.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}

      {dialog === "stop" ? (
        <Dialog
          title="Stop this workspace"
          onClose={close}
          footer={
            <>
              <button type="button" className="btn" onClick={close}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={stop.isPending}
                onClick={() =>
                  stop.mutate(undefined, {
                    onSuccess: () => {
                      setDialog(null);
                      onStopped?.();
                    },
                  })
                }
              >
                {stop.isPending ? "Stopping…" : "Stop it"}
              </button>
            </>
          }
        >
          <p className="dialog__text">
            Your {pluralize(workspace.gpus, "GPU")} go back to the pool
            immediately, ready for whoever needs them. Everything in your home
            folder is kept and waits for your next workspace; anything left in
            the fast local folder is lost.
          </p>
          {stop.isError ? (
            <p className="dialog__error" role="alert">
              {errorMessage(stop.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </div>
  );
}
